import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useAutoSave } from '../../hooks/useAutoSave';
import OnboardingProgress from '../../components/onboarding/OnboardingProgress';
import StepNavigation from '../../components/onboarding/StepNavigation';
import JobPreviewModal from '../../components/onboarding/JobPreviewModal';
import WelcomeGoals from './steps/WelcomeGoals';
import CareerProfile from './steps/CareerProfile';
import SkillsExperience from './steps/SkillsExperience';
import Preferences from './steps/Preferences';
import './OnboardingWizard.css';

const STEPS = [
  { number: 1, component: WelcomeGoals, required: true },
  { number: 2, component: CareerProfile, required: true },
  { number: 3, component: SkillsExperience, required: false },
  { number: 4, component: Preferences, required: false },
];

function OnboardingWizard() {
  const navigate = useNavigate();
  const {
    loading,
    completed,
    currentStep,
    data,
    saveStatus,
    saveStep,
    nextStep,
    prevStep,
    goToStep,
    completeOnboarding,
    skipToFinish,
    canProceed,
  } = useOnboarding();

  const [showJobModal, setShowJobModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Redirect if already completed
  useEffect(() => {
    if (!loading && completed) {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, completed, navigate]);

  // Initialize to step 1 if no step set
  useEffect(() => {
    if (!loading && currentStep === 0) {
      goToStep(1);
    }
  }, [loading, currentStep, goToStep]);

  // Get step-specific data for saving
  const getStepData = useCallback((step) => {
    switch (step) {
      case 1:
        return { primary_goal: data.primary_goal, full_name: data.full_name };
      case 2: {
        // Convert graduation_date from "YYYY-MM" to "YYYY-MM-01" for backend
        // Ensure empty strings become null to avoid Pydantic validation errors
        const graduationDate = data.graduation_date && data.graduation_date.length > 0
          ? (data.graduation_date.length === 7 ? `${data.graduation_date}-01` : data.graduation_date)
          : null;
        return {
          career_status: data.career_status || null,
          graduation_date: graduationDate,
          years_experience: data.years_experience || null,
          target_roles: data.target_roles || [],
          preferred_locations: data.preferred_locations || [],
          remote_preference: data.remote_preference || null,
        };
      }
      case 3:
        return {
          skills: data.skills,
          companies_worked: data.companies_worked,
          resume_uploaded: data.resume_uploaded,
          resume_path: data.resume_path,
        };
      case 4:
        return {
          email_notifications: data.email_notifications,
          theme_preference: data.theme_preference,
          feedback: data.feedback,
        };
      default:
        return {};
    }
  }, [data]);

  // Auto-save hook
  const handleAutoSave = useCallback(async () => {
    if (currentStep > 0) {
      await saveStep(currentStep, getStepData(currentStep));
    }
  }, [currentStep, saveStep, getStepData]);

  const { saveOnBlur } = useAutoSave(data, handleAutoSave, 30000, currentStep > 0);

  // Handle next step
  const handleNext = async () => {
    setSaving(true);

    // Save current step data
    const stepData = getStepData(currentStep);
    const result = await saveStep(currentStep, stepData);

    if (result.success) {
      if (currentStep === 4) {
        // Complete onboarding and show job modal
        const completeResult = await completeOnboarding();
        if (completeResult.success) {
          setShowJobModal(true);
        }
      } else {
        nextStep();
      }
    }

    setSaving(false);
  };

  // Handle back
  const handleBack = () => {
    prevStep();
  };

  // Handle skip to finish
  const handleSkip = async () => {
    setSaving(true);

    // Save current step first
    await saveStep(currentStep, getStepData(currentStep));

    // Skip remaining steps
    const result = await skipToFinish();
    if (result.success) {
      setShowJobModal(true);
    }

    setSaving(false);
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowJobModal(false);
    navigate('/dashboard', { replace: true });
  };

  // Loading state
  if (loading) {
    return (
      <div className="onboarding-loading">
        <div className="loading-spinner" />
        <p>Loading your progress...</p>
      </div>
    );
  }

  const StepComponent = STEPS[currentStep - 1]?.component;
  const isLastStep = currentStep === 4;
  const canProceedToNext = canProceed(currentStep);

  return (
    <div className="onboarding-wizard">
      {/* Header with logo */}
      <header className="onboarding-header">
        <div className="logo">
          <span className="logo-text">Careerly</span>
        </div>
        {saveStatus === 'saving' && (
          <span className="save-indicator saving">Saving...</span>
        )}
        {saveStatus === 'saved' && (
          <span className="save-indicator saved">Saved</span>
        )}
      </header>

      {/* Progress indicator */}
      <OnboardingProgress
        currentStep={currentStep}
        completedSteps={Array.from({ length: currentStep - 1 }, (_, i) => i + 1)}
      />

      {/* Step content */}
      <main className="onboarding-main" onBlur={saveOnBlur}>
        <div className="step-container">
          {StepComponent && <StepComponent />}
        </div>
      </main>

      {/* Navigation */}
      <StepNavigation
        currentStep={currentStep}
        totalSteps={4}
        onNext={handleNext}
        onBack={handleBack}
        onSkip={handleSkip}
        canProceed={canProceedToNext}
        loading={saving}
        isLastStep={isLastStep}
        showSkip={currentStep > 2}
      />

      {/* Job Preview Modal */}
      <JobPreviewModal
        isOpen={showJobModal}
        onClose={handleModalClose}
      />
    </div>
  );
}

export default OnboardingWizard;
